import React, {useState} from 'react';
import {
  Alert,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledButtonDropdown,
} from 'reactstrap';
import {useAuth} from 'contexts/AuthContext';
import {alertByError} from 'utils/alerts';
import {getDashboardSearchLimit} from 'api/search';
import {getAccountHistory} from 'api/accounts';
import {COMPANY_TYPE} from 'configs/types';
import {components} from 'react-select';
import {SearchAsyncBase} from '../FormBases';
import {map} from 'lodash';
import {getProgressColor} from 'components/Progress/ProgressStats';
import {getFormattedNumber} from 'utils/numbers';
import {useViewport} from 'contexts/ViewportContext';
import {VIEWPORT_WIDTHS} from 'configs';
import cx from 'classnames';
import styles from './styles.module.scss';
import {useMembership} from 'contexts/MembershipContext';
import MuiIcon from 'components/Icons/MuiIcon';

const SEARCH_TYPE = {
  company: 'company',
  certification: 'certification',
};

const SEARCH_LIMIT_TYPE = {
  company: 'ce',
  certification: 'cert',
};

const Control = ({children, ...props}) => {
  const {selectProps} = props;
  const {isCert, isMobile, onToggleMenu, onChangeSearchType} = selectProps;

  function handleMouseDown(e) {
    e.stopPropagation();
    onToggleMenu(false);
  }

  return (
    <components.Control {...props}>
      {isMobile && (
        <div
          className="pl-2 text-primary cursor-pointer"
          onMouseDown={(e) => handleMouseDown(e)}
          onTouchEnd={(e) => handleMouseDown(e)}
          style={{
            '&:focus': {
              border: 0,
            },
          }}
        >
          <UncontrolledButtonDropdown
            down="down"
            className="d-flex align-items-center"
          >
            <DropdownToggle
              color="white"
              className="p-0 m-0 text-primary small"
              size="sm"
              outline
            >
              <MuiIcon name={isCert ? 'insert_drive_file' : 'apartment'} />
              <MuiIcon className="ml-1" name="expand_more" />
            </DropdownToggle>

            <DropdownMenu className="py-0 mt-3 border">
              <DropdownItem
                className={cx(styles['search-select__dropdown--item'])}
                value="certified-entity"
                onClick={(e) => onChangeSearchType(e, false)}
              >
                Company Name
              </DropdownItem>
              <DropdownItem
                className={cx(styles['search-select__dropdown--item'])}
                value="certification"
                onClick={(e) => onChangeSearchType(e, false)}
              >
                Certification ID
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledButtonDropdown>
        </div>
      )}
      {children}
    </components.Control>
  );
};

function SearchSelectMenuList(props) {
  const {isTrial, subscriptionLevel} = useMembership();
  const {selectProps, isLoading, limit, ...rest} = props;
  const {inputValue} = selectProps;

  const {try: limitTry, max: limitMax} = limit || {};
  const limitReached = limitTry === limitMax;

  let text = limitReached ? (
    'You have insufficient credits.'
  ) : (
    <span>
      You have used{' '}
      <strong>
        {getFormattedNumber(limitTry)}/{getFormattedNumber(limitMax)}
      </strong>{' '}
      of your verifications
    </span>
  );

  const alertColor = getProgressColor(limitTry, limitMax);

  return (
    <div className="p-3 d-flex flex-column" style={{zIndex: 100}}>
      {isLoading ? (
        <span className="text-muted align-self-center">Loading...</span>
      ) : (
        <div>
          {!isTrial && subscriptionLevel <= 10 && !(limitMax < 0) && (
            <>
              <Alert fade={false} color={alertColor}>
                <MuiIcon
                  name={limitReached ? 'error' : 'info'}
                  className="mr-2"
                />
                {text}
              </Alert>

              <hr />
            </>
          )}

          {!inputValue && (
            <h6 className="font-weight-bold text-dark">Recent Searches</h6>
          )}

          <components.MenuList {...rest} />
        </div>
      )}
    </div>
  );
}

function SearchSelect(props) {
  const {
    type,
    additionalComponents,
    keyword,
    onChangeType,
    dropdownOpen,
    ...rest
  } = props;
  const {size} = useViewport();
  const {
    isAuth,
    isMainAccount,
    companyType,
    isFetchingSession,
    getSession,
    handleLoginBySession,
  } = useAuth();
  const isCert = type === SEARCH_TYPE.certification;
  const isAB = companyType === COMPANY_TYPE.ab;
  const isCompany = companyType === COMPANY_TYPE.company;
  const isCE = companyType === COMPANY_TYPE.ce;

  const isCompanyEnabledSubscription = isCompany || isCE;

  const [isLoading, setIsLoading] = useState(false);
  const [limit, setLimit] = useState({});
  const [recentSearch, setRecentSearch] = useState([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const isMobile = VIEWPORT_WIDTHS[size] < VIEWPORT_WIDTHS['xl'];
  const isViewportSmall = VIEWPORT_WIDTHS[size] < VIEWPORT_WIDTHS['sm'];

  async function getLimit() {
    try {
      // always get the new session to check updated plan
      const {token} = getSession();
      await handleLoginBySession(token);

      if (!isFetchingSession) {
        if (!isAB) {
          const {data} = await getDashboardSearchLimit(
            isMainAccount ? {type: SEARCH_LIMIT_TYPE[type]} : {}
          );

          setLimit(data);
        }
      }
    } catch (err) {
      await alertByError(err);
    }
  }

  async function getRecentSearches() {
    try {
      const {data} = await getAccountHistory({
        filter: {type: SEARCH_TYPE[type]},
      });

      if (isCert) {
        return setRecentSearch(
          map(data, (item) => ({
            cert_number: item.certification_number,
            ces: map(item.mdb_ces, (i) => i.company_name),
            company_certifications_id: item.company_certifications_id,
          }))
        );
      }

      setRecentSearch(data);
    } catch (err) {
      await alertByError(err);
    }
  }

  async function onMenuOpen() {
    setIsMenuOpen(!isMenuOpen);

    setIsLoading(true);
    if (isAuth) {
      await getLimit();

      if (isCompanyEnabledSubscription) {
        await getRecentSearches();
      }
    }
    setIsLoading(false);
  }

  const styles = {
    container: (styles) => {
      return {
        ...styles,
        ...(isViewportSmall && {
          position: 'static',
        }),
      };
    },
    control: (styles) => {
      return {
        ...styles,
        borderColor: '#0077c0',
        borderRadius: isMobile ? '50px 50px 50px 50px' : '0px 50px 50px 0px',
        '&:hover': {
          borderColor: null,
        },
      };
    },
    menu: (styles) => {
      return {
        ...styles,
        ...(isViewportSmall && {
          left: '0',
          top: '55px',
          width: 'calc(100% - 40px)',
          margin: '20px',
        }),
      };
    },
    placeholder: (styles) => ({
      ...styles,
      width: '100%',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
    }),
    input: (styles) => ({...styles, width: '0'}),
  };

  const selectComponents = {
    ...(isAuth && {Control}),
    IndicatorSeparator: null,
    DropdownIndicator: () => (
      <MuiIcon name="search" className="mr-2 text-primary" />
    ),
    ...(isCompanyEnabledSubscription && {
      MenuList: (props) => (
        <SearchSelectMenuList
          {...props}
          limit={limit}
          isCert={isCert}
          isLoading={isLoading}
          recentSearch={recentSearch}
        />
      ),
    }),
    Menu: (props) => {
      const inputValue = props.selectProps.inputValue;

      // Hide menu if input is empty
      if (!inputValue && isAB) return null;

      return <components.Menu {...props} />;
    },
    ...additionalComponents(limit),
  };

  const updatesProps = {
    styles,
    isCert,
    isMobile,
    menuIsOpen: isMenuOpen,
    onChangeSearchType: onChangeType,
    onMenuClose: () => setIsMenuOpen(false),
    onToggleMenu: (value) => setIsMenuOpen(value),
    onBlur: () => setIsMenuOpen(!!dropdownOpen && !isMenuOpen),
  };

  const searchProps = {
    cacheOptions: isCompanyEnabledSubscription && !keyword,
    components: selectComponents,
    onMenuOpen: onMenuOpen,
    defaultOptions: recentSearch,
    ...(isAuth && updatesProps),
    ...rest,
  };

  return <SearchAsyncBase {...searchProps} />;
}

export default SearchSelect;
